import { Component, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { IconComponent } from './ui/icon.component';
import { AnimateDirective } from '../directives/animate.directive';

@Component({
  selector: 'app-faq-v2',
  standalone: true,
  imports: [CommonModule, IconComponent, AnimateDirective],
  template: `
    <section id="faq" class="min-h-screen flex flex-col justify-center py-24 bg-gray-50">
      <div class="container mx-auto px-4 md:px-6 max-w-5xl">
        <div class="text-center mb-16">
          <div appAnimate class="inline-flex items-center justify-center w-20 h-20 rounded-full bg-[#111111] mb-6 shadow-xl text-[#FF5500] border-[5px] border-white ring-1 ring-gray-200">
            <app-icon name="chat-bubble" [size]="32" [strokeWidth]="1.5" class="fill-[#FF5500]/10"></app-icon>
          </div>
          <h2 appAnimate [delay]="100" class="text-3xl md:text-4xl font-bold text-[#111111] mb-4 tracking-tight">Perguntas Frequentes</h2>
          <p appAnimate [delay]="200" class="text-[#6B7280] text-lg md:text-xl max-w-3xl mx-auto font-light">Tire suas dúvidas com nossa assistente virtual.</p>
        </div>

        <div appAnimate [delay]="300" class="flex flex-wrap justify-center gap-3 mb-10">
          @for (cat of categories; track cat) {
            <button
              (click)="activeCategory.set(cat)"
              class="px-5 py-2.5 rounded-full text-sm font-bold transition-all shadow-sm"
              [class.bg-[#111111]]="activeCategory() === cat"
              [class.text-[#FF5500]]="activeCategory() === cat"
              [class.bg-white]="activeCategory() !== cat"
              [class.text-[#6B7280]]="activeCategory() !== cat"
              [class.border]="activeCategory() !== cat"
              [class.border-gray-200]="activeCategory() !== cat"
              [class.hover:border-[#FF5500]]="activeCategory() !== cat"
              [class.hover:text-[#111111]]="activeCategory() !== cat"
            >
              {{cat}}
            </button>
          }
        </div>

        <div class="space-y-4">
          @for (item of filteredFaqs(); track item.q; let i = $index) {
            <div appAnimate class="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden hover:shadow-md transition-shadow duration-300">
              <button (click)="toggle(item.q)" class="w-full px-6 py-5 text-left flex items-center justify-between hover:bg-gray-50 transition-colors gap-6">
                <span class="font-bold text-[#111111] text-base md:text-lg">{{item.q}}</span>
                <span class="text-gray-400 transition-transform duration-300 flex-shrink-0" [class.rotate-180]="isOpen(item.q)">
                  <app-icon name="chevron-down" [size]="20"></app-icon>
                </span>
              </button>
              <div class="overflow-hidden transition-all duration-300 ease-in-out"
                   [style.max-height.px]="isOpen(item.q) ? 500 : 0"
                   [style.opacity]="isOpen(item.q) ? 1 : 0">
                <div class="px-6 pb-6 text-[#6B7280] leading-relaxed border-t border-gray-100 pt-5 bg-gray-50/30 text-base">
                  {{item.a}}
                </div>
              </div>
            </div>
          }
        </div>


      </div>
    </section>
  `
})
export class FaqV2Component {
  categories = ['Jurídico', 'Técnico', 'Operacional', 'Instalação'];
  activeCategory = signal('Jurídico');

  // First 2 questions of Jurídico open by default
  openQuestions = signal<Set<string>>(new Set([
    'Minha empresa pode ser autuada mesmo tendo controle manual das pausas?',
    'Qual a diferença da sua solução para as demais já oferecidas pelo mercado?'
  ]));

  allFaqs = [
    // Jurídico
    {
      category: 'Jurídico',
      q: 'Minha empresa pode ser autuada mesmo tendo controle manual das pausas?',
      a: 'Sim. O Ministério do Trabalho e Emprego (MTE) exige que o registro seja feito por sistema automatizado e auditável. Controles manuais como planilhas, anotações ou relógios de ponto comuns não são aceitos como prova jurídica válida em processos trabalhistas. A beetrack é a única solução 100% homologada para este fim.'
    },
    {
      category: 'Jurídico',
      q: 'Qual a diferença da sua solução para as demais já oferecidas pelo mercado?',
      a: 'A solução da beetrack é a única desenvolvida exclusivamente para atender a demanda da NR 36, as demais que existem tentam entregar o que é necessário, mas acabam deixando lacunas jurídicas que afetam diretamente o resultado de uma ação trabalhista.'
    },
    {
      category: 'Jurídico',
      q: 'Que tipo de respaldo jurídico a solução oferece?',
      a: 'Primeiro todos os históricos de informações não podem ser manipulados na solução ou através de planilhas abertas, por meio dos relatórios que são impressos apenas em PDF e dashboards online é possível garantir qualquer questionamento técnico sobre a veracidade das informações.'
    },
    {
      category: 'Jurídico',
      q: 'Sua solução atende também a lei para área quente ou apenas áreas refrigeradas?',
      a: 'Nossa solução foi projetada para atender a NR 36 tanto para demanda de áreas frigorificadas como as áreas quentes.'
    },
    {
      category: 'Jurídico',
      q: 'Já existe alguma experiência de clientes que levaram a tribunais os relatórios da solução?',
      a: 'Sim, já tivemos vários elogios de advogados de clientes que abriram a solução online na hora da audiência e conseguiram comprovar que a empresa estava correta, zerando ações trabalhistas.'
    },
    {
      category: 'Jurídico',
      q: 'Como comprovo que entreguei a tag para o colaborador e que posso adverti-lo se não a usar no trabalho?',
      a: 'Sempre sugerimos que façam um controle de entrega similar ao de EPIs, ele dando ciência que está recebendo a Tag número xxxxx, que se compromete a usá-la no trabalho frigorificado e que de forma alguma poderá entregá-la a outra pessoa sob pena de advertência ou até posterior demissão por justa causa.'
    },

    // Técnico
    {
      category: 'Técnico',
      q: 'A solução de vocês é algum programa que preciso instalar nos meus computadores?',
      a: 'Nossa solução é 100% online e pode ser acessada de qualquer lugar, sem necessidade de qualquer instalação física ou remota em seus computadores.'
    },
    {
      category: 'Técnico',
      q: 'É possível entrar na solução e atualizar ou mudar algum histórico de serviço realizado?',
      a: 'A solução é 100% criptografada e segmentada na AWS, nem nossos engenheiros podem manipular as informações sem deixar o registro de LOG.'
    },
    {
      category: 'Técnico',
      q: 'Qual a duração média da bateria da tag e se é possível trocá-la?',
      a: 'Sim é possível trocar bateria, pois ela é similar as usadas em relógios (2032), sua duração depende muito da quantidade de uso da mesma, mas a média é de 4 a 6 meses.'
    },
    {
      category: 'Técnico',
      q: 'Posso usar qualquer Tag bluetooth do mercado?',
      a: 'Apesar da tecnologia Bluetooth já ser bem difundida no mercado nosso protocolo de uso é fechado para garantir precisão das leituras e estabilidade sistêmica, sendo assim cada tag ou gateway precisa ser oferecido e entregue pela beetrack.'
    },
    {
      category: 'Técnico',
      q: 'Como a solução garante que o colaborador realmente entrou ou saiu da câmara fria?',
      a: 'Através de algoritmos refinados que a solução possuí é possível ter a certeza quando alguém só passou na porta e não entrou ou se realmente ele entrou na câmara fria.'
    },
    {
      category: 'Técnico',
      q: 'Se faltar luz ou internet na empresa, como fica meu controle?',
      a: 'No caso se falta de internet possuímos alguns gateways com buffer "memória", lembrar sempre de informar essa demanda antes da precificação, pois a precificação padrão vai sem memória. Quanto a falta de luz, dificilmente nos pedem, pois normalmente a operação para também, apesar de existir uma forma de contingência, sugiro que questione nosso especialista. Importante: se nos dois casos não tiver acesso à solução, sugerirmos que use neste momento o papel e guardem para futuros questionamentos.'
    },

    // Operacional
    {
      category: 'Operacional',
      q: 'Como minha equipe operacional é avisada que precisa sair da área gelada ou quente?',
      a: 'Através de alertas visuais e sonoros em telas que podem instalar as áreas, alertas via Whatsapp que podem ser previamente configurados e pode-se escolher Tags com alertas sonoros e de vibração.'
    },
    {
      category: 'Operacional',
      q: 'Já que a solução controla o trabalho dos colaboradores, pode acontecer de reduzir produtividade da empresa?',
      a: 'Pelo contrário, tivemos vários depoimentos de empresas que alegaram que sua produtividade depois de 6 meses de uso melhorou em 35%.'
    },
    {
      category: 'Operacional',
      q: 'Como faço para cadastrar novas pessoas na solução?',
      a: 'De forma bem simples e intuitiva, basta acessar o campo de cadastro, inclusive se precisar cadastrar muitos nomes de uma só vez esse mesmo campo possui uma ferramenta para auxiliá-los.'
    },
    {
      category: 'Operacional',
      q: 'Existe algum tipo de alerta de emergência que pode ser acionado?',
      a: 'Existem Tags com ou sem esse botão de emergência, sugiro que questione nosso especialista sobre sua necessidade.'
    },
    {
      category: 'Operacional',
      q: 'Consigo bloquear acesso de pessoas não autorizadas a área frigorificada?',
      a: 'Sim, é possível cadastrar tags na solução e configurá-las como não permitidas, caso alguém entre na câmara fria usando essa tag, será alarmado.'
    },

    // Instalação
    {
      category: 'Instalação',
      q: 'Em quanto tempo minha operação já está monitorada?',
      a: 'Em média 48 horas após a contratação. Nosso time realiza a instalação remota ou presencial, e o sistema já começa a registrar e documentar as pausas no mesmo dia da ativação.'
    },
    {
      category: 'Instalação',
      q: 'É muito difícil instalar as antenas na empresa?',
      a: 'Sempre que fechamos um novo contrato nossa área de suporte auxilia nesta instalação de forma remota, pois trata-se de uma instalação super simples, um pouco diferente, mas similar a um novo gateway de wifi para sua empresa.'
    },
    {
      category: 'Instalação',
      q: 'Minha empresa fica em outro estado, como faço para instalar a solução?',
      a: 'Nossa solução foi desenvolvida justamente para atender de forma remota vários tipos de demandas, desta forma nós enviamos os equipamentos previamente configurados e sua empresa só precisa seguir alguns passos de instalação para já sair controlando sua equipe.'
    },
  ];

  filteredFaqs() {
    return this.allFaqs.filter(f => f.category === this.activeCategory());
  }

  isOpen(q: string) {
    return this.openQuestions().has(q);
  }

  toggle(q: string) {
    this.openQuestions.update(set => {
      const newSet = new Set(set);
      if (newSet.has(q)) newSet.delete(q);
      else newSet.add(q);
      return newSet;
    });
  }
}
